import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { ArrowRight, Check, Crown, Shield, TrendingUp, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";
const HERO_IMG = "https://images.unsplash.com/photo-1503891450247-ee5f8ec46dc3?w=1400&q=80";

const STRIPE_URLS = {
  general: "https://buy.stripe.com/test_3cI3cw76afo3dj8ekM2cg00",
  private_access: "https://buy.stripe.com/test_3cIcN61LQ3Fl92SfoQ2cg01",
};

const LANE_INFO = {
  budget: { name: "Budget Tier", tagline: "Matched to our sourcing network", desc: "Book a consultation to discuss your options and get matched with the right suppliers for your budget.", icon: TrendingUp, consultation: { type: "general", price: 50, duration: "15-minute", title: "General Consultation" } },
  premium: { name: "Premium Tier", tagline: "KookaMF Network \u2014 Curated for you", desc: "You qualify for curated options from our exclusive KookaMF network. Book a consultation to explore premium sourcing.", icon: Shield, consultation: { type: "general", price: 50, duration: "15-minute", title: "General Consultation" } },
  boutique: { name: "Boutique Access", tagline: "Direct cultivator introductions via KookaMF", desc: "You qualify for private cultivator access through our exclusive KookaMF partnership. This is approval-based \u2014 not all applicants are accepted.", icon: Crown, consultation: { type: "private_access", price: 150, duration: "45-minute", title: "Private Cultivator Access" } },
};

export default function ResultPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubmission = useCallback(async () => {
    try { const res = await axios.get(`${API}/submissions/${submissionId}`); setSubmission(res.data); }
    catch { toast.error("Failed to load result"); navigate("/"); }
    finally { setLoading(false); }
  }, [submissionId, navigate]);

  useEffect(() => { fetchSubmission(); }, [fetchSubmission]);

  const handleBookConsultation = () => {
    if (!submission) return;
    const lane = LANE_INFO[submission.lane];
    if (!lane) return;
    const stripeUrl = new URL(STRIPE_URLS[lane.consultation.type]);
    stripeUrl.searchParams.set("client_reference_id", submissionId);
    if (submission.email) stripeUrl.searchParams.set("prefilled_email", submission.email);
    window.location.href = stripeUrl.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto" style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!submission) return null;
  const lane = LANE_INFO[submission.lane];
  if (!lane) return null;
  const LaneIcon = lane.icon;
  const isPaid = submission.payment_status === "paid";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="result-page">
      {/* Hero */}
      <div className="relative" style={{ height: "clamp(180px, 30vh, 280px)" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,50,33,0.6) 0%, rgba(15,50,33,0.88) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-8 md:px-12 lg:px-20 py-6 md:py-8">
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-9 sm:h-10 md:h-12 self-start logo-on-dark" /></a>
          <div>
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>Your Routing Result</p>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tight" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }} data-testid="lane-title">
              {lane.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "rgba(253,252,247,0.6)" }}>{lane.tagline}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex justify-center px-5 sm:px-8 md:px-12 lg:px-20 py-10 md:py-14">
        <div className="w-full max-w-xl">
          <p className="text-sm md:text-base leading-relaxed mb-8" style={{ color: "var(--text-secondary)" }} data-testid="lane-description">{lane.desc}</p>

          {isPaid ? (
            <div className="p-6 md:p-8 text-center" style={{ background: "rgba(46,92,58,0.06)", border: "1px solid rgba(46,92,58,0.15)" }} data-testid="payment-confirmed">
              <Check size={32} className="mx-auto mb-3" style={{ color: "var(--success)" }} />
              <p className="font-medium text-lg" style={{ color: "var(--success)" }}>
                {submission.lane === "boutique" ? "Request Submitted \u2014 Pending Review" : "Consultation Booked"}
              </p>
              <p className="text-sm mt-2" style={{ color: "var(--text-tertiary)" }}>
                {submission.lane === "boutique" ? "Your application is under review. We\u2019ll contact you within 48 hours." : "We\u2019ll reach out within 24 hours to schedule your session."}
              </p>
            </div>
          ) : (
            <div style={{ border: "1px solid var(--border)" }} data-testid="consultation-offer">
              <div className="p-6 md:p-8" style={{ background: "var(--primary)" }}>
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-3">
                    <LaneIcon size={22} strokeWidth={1.5} style={{ color: "#C2693D" }} />
                    <p className="font-medium text-base" style={{ color: "#F5F3E9" }}>{lane.consultation.title}</p>
                  </div>
                  <p className="text-2xl font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F3E9" }}>${lane.consultation.price}</p>
                </div>
                <p className="text-xs mt-1 ml-9" style={{ color: "rgba(245,243,233,0.5)" }}>{lane.consultation.duration} session</p>
              </div>

              <div className="p-6 md:p-8" style={{ background: "var(--bg-elevated, #fff)" }}>
                {submission.lane === "boutique" && (
                  <div className="flex items-start gap-3 p-4 mb-5" style={{ background: "rgba(194,105,61,0.06)", border: "1px solid rgba(194,105,61,0.15)" }}>
                    <Lock size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#C2693D" }} />
                    <p className="text-xs" style={{ color: "var(--text-secondary)" }}>Private, approval-based service. No introductions or sourcing details shared without approval.</p>
                  </div>
                )}
                {submission.lane !== "boutique" && (
                  <p className="text-xs mb-5" style={{ color: "var(--text-tertiary)", fontStyle: "italic" }}>Extended session pricing may apply if consultation exceeds 30 minutes.</p>
                )}
                <button onClick={handleBookConsultation} data-testid="btn-book-consultation"
                  className="w-full py-4 text-sm font-semibold tracking-wide flex items-center justify-center gap-2"
                  style={{ background: "var(--primary)", color: "#F5F3E9", transition: "background-color 0.2s ease" }}>
                  Book {lane.consultation.title} <ArrowRight size={16} />
                </button>
                <p className="text-xs text-center mt-4" style={{ color: "var(--text-tertiary)" }}>Consultation fees are non-refundable.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <SiteFooter submissionId={submission.id} />
    </div>
  );
}
